import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  QueryList,
  ViewChildren,
  contentChildren,
  effect,
  input,
  model,
} from '@angular/core';
import { IconComponent } from 'src/app/ui-kit/icon/icon.component';
import { TabPanelComponent } from './tab-panel.component';
import { TABS_PARENT } from './tabs.token';

/** Visual variant for the tab triggers. */
export type TabsVariant = 'underline' | 'segmented' | 'folder';

/**
 * Accessible WAI-ARIA tabs. Compose with `<app-tab-panel>` children — the tab
 * trigger list is generated automatically from their `tabId` / `label` / `icon` inputs.
 *
 * Keyboard: Arrow Left/Right move between enabled tabs, Home/End jump to first/last,
 * Enter/Space activate. Panels are shown/hidden via each panel's own host `[hidden]`
 * binding which reads the active tab via DI.
 *
 * Variants: `underline` (default), `segmented` (pill track), and `folder` — a
 * manila-folder treatment where the active trigger joins a bordered panel box,
 * for surfaces that present mutually exclusive choices side by side.
 *
 * @example
 * <app-tabs [(activeTab)]="tab" ariaLabel="Student details">
 *   <app-tab-panel tabId="profile" label="Profile">...</app-tab-panel>
 *   <app-tab-panel tabId="goals" label="Goals" icon="flag">...</app-tab-panel>
 *   <app-tab-panel tabId="notes" label="Notes" [disabled]="true">...</app-tab-panel>
 * </app-tabs>
 *
 * @feature shared
 */
@Component({
  selector: 'app-tabs',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  host: { class: 'block' },
  providers: [
    {
      provide: TABS_PARENT,
      useExisting: TabsComponent,
    },
  ],
  template: `
    <div
      role="tablist"
      [attr.aria-label]="ariaLabel()"
      class="flex gap-1"
      [class.flex-wrap]="variant() === 'folder'"
      [class.border-b]="variant() === 'underline'"
      [class.border-outline]="variant() === 'underline'"
      [class.rounded-full]="variant() === 'segmented'"
      [class.bg-surface-low]="variant() === 'segmented'"
      [class.p-1]="variant() === 'segmented'"
    >
      @for (tab of tabs(); track tab.tabId(); let i = $index) {
        <button
          #trigger
          type="button"
          role="tab"
          [id]="'tab-' + tab.tabId()"
          [attr.aria-selected]="activeTab() === tab.tabId()"
          [attr.aria-controls]="'panel-' + tab.tabId()"
          [attr.tabindex]="activeTab() === tab.tabId() ? 0 : -1"
          [disabled]="tab.disabled()"
          (click)="onActivate(tab.tabId(), i)"
          (keydown)="onKeyDown($event, i)"
          class="inline-flex items-center gap-2 px-4 font-semibold text-sm transition-colors focus-visible:outline-none focus-visible:shadow-[var(--theme-focus-ring)]"
          [class.py-2]="variant() !== 'folder'"
          [class.py-3]="variant() === 'folder'"
          [class.text-muted]="activeTab() !== tab.tabId() && !tab.disabled()"
          [class.text-disabled]="tab.disabled()"
          [class.text-primary]="activeTab() === tab.tabId() && !tab.disabled()"
          [class.cursor-not-allowed]="tab.disabled()"
          [class.border-b-2]="variant() === 'underline'"
          [class.border-transparent]="variant() !== 'segmented' && activeTab() !== tab.tabId()"
          [class.border-primary]="variant() === 'underline' && activeTab() === tab.tabId()"
          [class.-mb-px]="variant() === 'underline' || (variant() === 'folder' && activeTab() === tab.tabId())"
          [class.rounded-full]="variant() === 'segmented'"
          [class.bg-surface]="variant() === 'segmented' && activeTab() === tab.tabId()"
          [class.shadow-sm]="variant() === 'segmented' && activeTab() === tab.tabId()"
          [class.rounded-t-xl]="variant() === 'folder'"
          [class.border]="variant() === 'folder'"
          [class.border-outline]="variant() === 'folder' && activeTab() === tab.tabId()"
          [class.border-b-0]="variant() === 'folder' && activeTab() === tab.tabId()"
          [class.bg-surface-low]="variant() === 'folder' && activeTab() === tab.tabId()"
          [class.relative]="variant() === 'folder' && activeTab() === tab.tabId()"
          [class.z-10]="variant() === 'folder' && activeTab() === tab.tabId()"
        >
          @if (tab.icon()) {
            <app-icon [name]="tab.icon()!" size="xs" />
          }
          <span>{{ tab.label() }}</span>
        </button>
      }
    </div>

    <!-- The folder variant turns the body into the folder itself: the active
         trigger overlaps this box's top border (-mb-px + z-10) so the two read
         as one continuous shape. -->
    <div
      [class.pt-4]="variant() !== 'folder'"
      [class.rounded-b-2xl]="variant() === 'folder'"
      [class.rounded-tr-2xl]="variant() === 'folder'"
      [class.border]="variant() === 'folder'"
      [class.border-outline]="variant() === 'folder'"
      [class.bg-surface-low]="variant() === 'folder'"
      [class.p-5]="variant() === 'folder'"
    >
      <ng-content />
    </div>
  `,
})
export class TabsComponent {
  /** Projected `<app-tab-panel>` children. */
  readonly tabs = contentChildren(TabPanelComponent);

  /** Two-way bound active tab id. */
  readonly activeTab = model<string>('');

  /** Visual variant. @default 'underline' */
  readonly variant = input<TabsVariant>('underline');

  /** Accessible label for the tablist. */
  readonly ariaLabel = input<string | undefined>(undefined);

  @ViewChildren('trigger') private triggers?: QueryList<ElementRef<HTMLButtonElement>>;

  constructor() {
    // Default to the first enabled tab if nothing is selected or the selection is invalid.
    effect(
      () => {
        const tabs = this.tabs();
        if (!tabs.length) return;
        const current = this.activeTab();
        const hasCurrent = tabs.some(t => t.tabId() === current && !t.disabled());
        if (!current || !hasCurrent) {
          const firstEnabled = tabs.find(t => !t.disabled());
          if (firstEnabled) this.activeTab.set(firstEnabled.tabId());
        }
      },
      { allowSignalWrites: true },
    );
  }

  onActivate(id: string, index: number): void {
    const tab = this.tabs()[index];
    if (tab?.disabled()) return;
    this.activeTab.set(id);
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    const tabs = this.tabs();
    if (!tabs.length) return;
    let next = index;
    switch (event.key) {
      case 'ArrowRight':
        next = this.nextEnabled(index, 1);
        break;
      case 'ArrowLeft':
        next = this.nextEnabled(index, -1);
        break;
      case 'Home':
        next = this.nextEnabled(-1, 1);
        break;
      case 'End':
        next = this.nextEnabled(tabs.length, -1);
        break;
      default:
        return;
    }
    event.preventDefault();
    const tab = tabs[next];
    this.activeTab.set(tab.tabId());
    queueMicrotask(() => {
      const btn = this.triggers?.get(next)?.nativeElement;
      btn?.focus();
    });
  }

  private nextEnabled(from: number, step: number): number {
    const tabs = this.tabs();
    const len = tabs.length;
    let i = from;
    for (let n = 0; n < len; n++) {
      i = (i + step + len) % len;
      if (!tabs[i].disabled()) return i;
    }
    return Math.max(0, from);
  }
}
